import { create } from 'zustand'
import type { User } from 'firebase/auth'
import type { Profile, TeamAdmin } from '@/types'
import { type Role, getRole } from '@/lib/rbac'
import { markTeamSwitchedForChargebee } from '@/lib/chargebee'

interface AuthState {
  firebaseUser: User | null
  profile: Profile | null
  teamAdmins: TeamAdmin[]
  selectedTeamId: string | null
  role: Role
  isGlobalAdmin: boolean
  isAuthenticated: boolean
  isLoading: boolean
  // Kept outside `initialState` (see below) so `reset()`'s merge doesn't clear it — it needs
  // to survive the sign-out/reset that accompanies setting it, all the way to the LoginPage
  // render that follows the redirect.
  notAdminError: boolean
  // True for the brief window between a real sign-in/sign-up resolving on the invite-redemption
  // path and redeemTeamAdminInvite settling — see LoginPage. useBootstrap's "not an admin of any
  // team" kickout checks this before acting, so a fresh account (zero team_admin rows yet) isn't
  // bounced back to /login by a stale teamAdmins fetch that raced ahead of the redemption itself.
  redeemingInvite: boolean
  // True from the moment setSelectedTeamId actually changes the id until useBootstrap's next
  // GET_TEAM_ADMINS_BY_PROFILE_ID response has processed. teamAdmins/team/branding are separate
  // stores that don't update atomically with selectedTeamId — switching teams (BrandSwitcher,
  // AddAccountModal) can leave useIsInternalAdmin briefly reading false for the new team before
  // its real teamAdmins row has loaded, which flashed StartTrialModal instead of NoTrialBanner
  // right after creating a demo account. Layout.tsx's noPlan gating waits on this the same way it
  // already waits on useBootstrap's teamLoading.
  switchingTeam: boolean

  setFirebaseUser: (user: User | null) => void
  setProfile: (profile: Profile | null) => void
  setTeamAdmins: (admins: TeamAdmin[]) => void
  setSelectedTeamId: (teamId: string | null) => void
  setNotAdminError: (value: boolean) => void
  setRedeemingInvite: (value: boolean) => void
  reset: () => void
}

const SELECTED_TEAM_KEY = 'rolla_selected_team_id'

const initialState = {
  firebaseUser: null,
  profile: null,
  teamAdmins: [],
  selectedTeamId: localStorage.getItem(SELECTED_TEAM_KEY),
  role: 'admin' as Role,
  isGlobalAdmin: false,
  isAuthenticated: false,
  isLoading: true,
}

export const useAuthStore = create<AuthState>((set) => ({
  ...initialState,
  notAdminError: false,
  redeemingInvite: false,
  switchingTeam: false,

  setFirebaseUser: (user) =>
    set({
      firebaseUser: user,
      // Anonymous sessions (see lib/firebase.ts) exist only to carry a token for signed-out
      // screens — they must never satisfy ProtectedRoute.
      isAuthenticated: !!user && !user.isAnonymous,
      isLoading: false,
    }),

  setProfile: (profile) =>
    set((state) => {
      const isGlobalAdmin = !!profile?.global_admin
      const teamRole = state.teamAdmins.find((ta) => ta.teamId === state.selectedTeamId)?.role
      return {
        profile,
        isGlobalAdmin,
        role: getRole(isGlobalAdmin, teamRole),
      }
    }),

  setTeamAdmins: (admins) =>
    set((state) => {
      const selectedTeamId = state.selectedTeamId ?? admins[0]?.teamId ?? null
      const teamRole = admins.find((ta) => ta.teamId === selectedTeamId)?.role
      return {
        teamAdmins: admins,
        selectedTeamId,
        role: getRole(state.isGlobalAdmin, teamRole),
        switchingTeam: false,
      }
    }),

  setSelectedTeamId: (teamId) =>
    set((state) => {
      if (teamId) localStorage.setItem(SELECTED_TEAM_KEY, teamId)
      else localStorage.removeItem(SELECTED_TEAM_KEY)
      const teamRole = state.teamAdmins.find((ta) => ta.teamId === teamId)?.role
      return {
        selectedTeamId: teamId,
        role: getRole(state.isGlobalAdmin, teamRole),
        switchingTeam: teamId != null && teamId !== state.selectedTeamId,
      }
    }),

  setNotAdminError: (value) => set({ notAdminError: value }),
  setRedeemingInvite: (value) => set({ redeemingInvite: value }),

  // Spreading initialState alone isn't enough here — its selectedTeamId was captured once from
  // localStorage at module load, so a reset() without this would silently restore whichever team
  // was selected when the tab first opened (e.g. a just-signed-out user's team), not clear it.
  // Real-world case: sign out, then sign in as a different account in the same tab — without
  // this, the new account would briefly carry the previous account's selectedTeamId, misdirecting
  // any team-scoped mutation fired before useBootstrap resolves the real one.
  reset: () => {
    localStorage.removeItem(SELECTED_TEAM_KEY)
    set({ ...initialState, selectedTeamId: null, isLoading: false, switchingTeam: false })
    // Same-tab sign-out/sign-in-as-different-account has the exact same Chargebee.js staleness
    // issue as BrandSwitcher's team switch — see markTeamSwitchedForChargebee's comment.
    markTeamSwitchedForChargebee()
  },
}))
